import i18n from "../../i18n";
import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { farmDateIso } from "@/lib/farmTime";

const DOC_TYPES = ["resume", "contract", "government_id", "passport", "driver_license", "certificate", "training", "police_clearance", "medical", "visa", "work_permit", "warning", "award", "photo", "other"];
const COURSE_CATEGORIES = ["safety", "animal_welfare", "biosecurity", "machinery", "chemical_handling", "first_aid", "fire_safety", "food_safety", "livestock", "vaccination", "leadership", "compliance", "orientation", "other"];
const ENROLL_STATUSES = ["assigned", "in_progress", "completed", "failed", "cancelled"];

function Field({ label, children }) {
    return <div><div className="text-xs text-muted-foreground mb-1">{label}</div>{children}</div>;
}

export function PositionDialog({ initial, positions, employees, onSave, onClose }) {
    const [f, setF] = useState({ title: "", department: "", parent_id: "", employee_id: "", employee_name: "", ...initial });
    const set = (k, v) => setF({ ...f, [k]: v });
    return (
        <Dialog open onOpenChange={onClose}><DialogContent className="max-w-md" data-testid="position-dialog">
            <DialogHeader>
                <DialogTitle>{f.id ? "Edit Position" : "New Position"}</DialogTitle>
                <DialogDescription>{i18n.t("batch1.7b1c510683d9")}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
                <Field label={i18n.t("batch1.db83dc176235")}><Input value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="position-title-input" /></Field>
                <Field label={i18n.t("batch1.db40106a4051")}><Input value={f.department} onChange={(e) => set("department", e.target.value)} placeholder={i18n.t("batch1.dc224b39292d")} /></Field>
                <Field label={i18n.t("batch1.655fe3daa8e4")}>
                    <Select value={f.parent_id || "__none__"} onValueChange={(v) => set("parent_id", v === "__none__" ? "" : v)}>
                        <SelectTrigger data-testid="position-parent-select"><SelectValue placeholder={i18n.t("batch1.5c7113e38a37")} /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__none__">{i18n.t("batch1.5c7113e38a37")}</SelectItem>
                            {positions.filter(p => p.id !== f.id).map((p) => (
                                <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </Field>
                <Field label={i18n.t("batch1.5226bf9ebf6a")}>
                    <Select value={f.employee_id || "__none__"} onValueChange={(v) => set("employee_id", v === "__none__" ? "" : v)}>
                        <SelectTrigger><SelectValue placeholder={i18n.t("batch1.3b906ae77c48")} /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__none__">{i18n.t("batch1.3b906ae77c48")}</SelectItem>
                            {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </Field>
            </div>
            <DialogFooter>
                <Button variant="outline" onClick={onClose}>{i18n.t("batch1.77dfd2135f4d")}</Button>
                <Button
                    onClick={() => f.title.trim() ? onSave(f) : toast.error("Title required")}
                    data-testid="save-position-btn"
                >{i18n.t("batch1.efc007a393f6")}</Button>
            </DialogFooter>
        </DialogContent></Dialog>
    );
}

export function DocumentDialog({ initial, employees, onSave, onClose }) {
    const [f, setF] = useState({ employee_id: "", doc_type: "contract", file_name: "", file_url: "", issued_date: "", expiry_date: "", notes: "", ...initial });
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);
    const set = (k, v) => setF({ ...f, [k]: v });

    const handleFile = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const form = new FormData();
            form.append("file", file);
            form.append("category", "HR Documents");
            const r = await api.post("/drive/upload", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setF((cur) => ({
                ...cur,
                file_url: r.data.web_view_link || r.data.webViewLink || cur.file_url,
                file_name: r.data.name || file.name,
            }));
            toast.success(`Uploaded to Drive → HR Documents: ${r.data.name}`);
        } catch (e) {
            const msg = e.response?.data?.detail || "Upload failed — is Google Drive connected?";
            toast.error(msg);
        } finally { setUploading(false); }
    };

    return (
        <Dialog open onOpenChange={onClose}><DialogContent className="max-w-md" data-testid="document-dialog">
            <DialogHeader>
                <DialogTitle>{f.id ? "Edit Document" : "Add Employee Document"}</DialogTitle>
                <DialogDescription>{i18n.t("batch1.8f3582ac4ca2")}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
                <Field label={i18n.t("batch1.01f09d033582")}>
                    <Select value={f.employee_id} onValueChange={(v) => set("employee_id", v)}>
                        <SelectTrigger data-testid="doc-employee-select"><SelectValue placeholder={i18n.t("batch1.c3b69e661eec")} /></SelectTrigger>
                        <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                    </Select>
                </Field>
                <Field label={i18n.t("batch1.300b6ef0cdd8")}>
                    <Select value={f.doc_type} onValueChange={(v) => set("doc_type", v)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{DOC_TYPES.map((t) => (
                            <SelectItem key={t} value={t} className="capitalize">{t.replace(/_/g, " ")}</SelectItem>
                        ))}</SelectContent>
                    </Select>
                </Field>
                <div className="rounded-lg border-2 border-dashed p-3 bg-muted/30">
                    <div className="text-xs text-muted-foreground mb-2">{i18n.t("batch1.e38e46045f4a")}</div>
                    <input
                        ref={fileRef}
                        type="file"
                        className="hidden"
                        onChange={(e) => handleFile(e.target.files?.[0])}
                        data-testid="doc-file-input"
                    />
                    <div className="flex gap-2 items-center">
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={uploading}
                            onClick={() => fileRef.current?.click()}
                            data-testid="doc-upload-btn"
                        >
                            {uploading
                                ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />{i18n.t("batch1.d921a79afac4")}</>
                                : <><Upload className="w-4 h-4 mr-1" />{i18n.t("batch1.1af75126f6bf")}</>}
                        </Button>
                        <span className="text-xs text-muted-foreground truncate">{f.file_name || "No file chosen"}</span>
                    </div>
                </div>
                <Field label={i18n.t("batch1.a2d22fa79cfa")}><Input value={f.file_url} onChange={(e) => set("file_url", e.target.value)} data-testid="doc-url-input" placeholder={i18n.t("batch1.49cc9506e074")} /></Field>
                <div className="grid grid-cols-2 gap-2">
                    <Field label={i18n.t("batch1.deb5d5e2f038")}><Input type="date" value={f.issued_date} onChange={(e) => set("issued_date", e.target.value)} /></Field>
                    <Field label={i18n.t("batch1.a99be3da0c9d")}><Input type="date" value={f.expiry_date} onChange={(e) => set("expiry_date", e.target.value)} data-testid="doc-expiry-input" /></Field>
                </div>
                <Field label={i18n.t("batch1.70440046a3dc")}><Textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
            </div>
            <DialogFooter>
                <Button variant="outline" onClick={onClose}>{i18n.t("batch1.77dfd2135f4d")}</Button>
                <Button
                    onClick={() => f.employee_id ? onSave(f) : toast.error("Employee required")}
                    data-testid="save-document-btn"
                >{i18n.t("batch1.efc007a393f6")}</Button>
            </DialogFooter>
        </DialogContent></Dialog>
    );
}

export function CourseDialog({ initial, onSave, onClose }) {
    const [f, setF] = useState({ name: "", category: "safety", trainer: "", validity_months: "", description: "", cost: "", ...initial });
    const set = (k, v) => setF({ ...f, [k]: v });
    return (
        <Dialog open onOpenChange={onClose}><DialogContent className="max-w-md" data-testid="course-dialog">
            <DialogHeader><DialogTitle>{f.id ? "Edit Course" : "New Training Course"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
                <Field label={i18n.t("batch1.3e98a6f0ad01")}><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="course-name-input" placeholder={i18n.t("batch1.24b572840e97")} /></Field>
                <div className="grid grid-cols-2 gap-2">
                    <Field label={i18n.t("batch1.a3c686e711e4")}>
                        <Select value={f.category} onValueChange={(v) => set("category", v)}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{COURSE_CATEGORIES.map((c) => (
                                <SelectItem key={c} value={c} className="capitalize">{c.replace(/_/g, " ")}</SelectItem>
                            ))}</SelectContent>
                        </Select>
                    </Field>
                    <Field label={i18n.t("batch1.d780da02aeb5")}><Input type="number" value={f.validity_months} onChange={(e) => set("validity_months", e.target.value)} placeholder={i18n.t("batch1.4ffa6666f3d0")} /></Field>
                </div>
                <Field label={i18n.t("batch1.63deca910cbd")}><Input value={f.trainer} onChange={(e) => set("trainer", e.target.value)} /></Field>
                <Field label={i18n.t("batch1.b13bdb89caf8")}><Input type="number" value={f.cost} onChange={(e) => set("cost", e.target.value)} /></Field>
                <Field label={i18n.t("batch1.55f8ebc805e6")}><Textarea rows={2} value={f.description} onChange={(e) => set("description", e.target.value)} /></Field>
            </div>
            <DialogFooter>
                <Button variant="outline" onClick={onClose}>{i18n.t("batch1.77dfd2135f4d")}</Button>
                <Button
                    onClick={() => f.name.trim() ? onSave(f) : toast.error("Name required")}
                    data-testid="save-course-btn"
                >{i18n.t("batch1.efc007a393f6")}</Button>
            </DialogFooter>
        </DialogContent></Dialog>
    );
}

export function EnrollmentDialog({ initial, employees, courses, onSave, onClose }) {
    const [f, setF] = useState({
        employee_id: "", course_id: "",
        start_date: farmDateIso(),
        completion_date: "", expiry_date: "", status: "assigned",
        score: "", certificate_url: "", notes: "", ...initial,
    });
    const set = (k, v) => setF({ ...f, [k]: v });
    const applyCourse = (cid) => {
        const c = courses.find(x => x.id === cid);
        if (c?.validity_months && f.completion_date && !f.expiry_date) {
            const d = new Date(f.completion_date);
            d.setMonth(d.getMonth() + Number(c.validity_months));
            setF((cur) => ({ ...cur, course_id: cid, expiry_date: farmDateIso(d) }));
        } else {
            set("course_id", cid);
        }
    };
    return (
        <Dialog open onOpenChange={onClose}><DialogContent className="max-w-md" data-testid="enrollment-dialog">
            <DialogHeader><DialogTitle>{f.id ? "Edit Enrollment" : "Enroll Employee"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
                <Field label={i18n.t("batch1.01f09d033582")}>
                    <Select value={f.employee_id} onValueChange={(v) => set("employee_id", v)}>
                        <SelectTrigger data-testid="enrollment-employee-select"><SelectValue placeholder={i18n.t("batch1.c3b69e661eec")} /></SelectTrigger>
                        <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                    </Select>
                </Field>
                <Field label={i18n.t("batch1.231bdaee7939")}>
                    <Select value={f.course_id} onValueChange={applyCourse}>
                        <SelectTrigger data-testid="enrollment-course-select"><SelectValue placeholder={i18n.t("batch1.c3b69e661eec")} /></SelectTrigger>
                        <SelectContent>{courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                </Field>
                <div className="grid grid-cols-3 gap-2">
                    <Field label={i18n.t("batch1.952f375412e8")}><Input type="date" value={f.start_date} onChange={(e) => set("start_date", e.target.value)} /></Field>
                    <Field label={i18n.t("batch1.1798b3ba42ee")}><Input type="date" value={f.completion_date} onChange={(e) => set("completion_date", e.target.value)} /></Field>
                    <Field label={i18n.t("batch1.a99be3da0c9d")}><Input type="date" value={f.expiry_date} onChange={(e) => set("expiry_date", e.target.value)} /></Field>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <Field label={i18n.t("batch1.bae7d5be7082")}>
                        <Select value={f.status} onValueChange={(v) => set("status", v)}>
                            <SelectTrigger data-testid="enrollment-status-select"><SelectValue /></SelectTrigger>
                            <SelectContent>{ENROLL_STATUSES.map((s) => (
                                <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>
                            ))}</SelectContent>
                        </Select>
                    </Field>
                    <Field label={i18n.t("batch1.489f4877244a")}><Input type="number" value={f.score} onChange={(e) => set("score", e.target.value)} placeholder="0-100" /></Field>
                </div>
                <Field label={i18n.t("batch1.8733b2d77b51")}><Input value={f.certificate_url} onChange={(e) => set("certificate_url", e.target.value)} placeholder={i18n.t("batch1.49cc9506e074")} /></Field>
            </div>
            <DialogFooter>
                <Button variant="outline" onClick={onClose}>{i18n.t("batch1.77dfd2135f4d")}</Button>
                <Button
                    onClick={() => (f.employee_id && f.course_id) ? onSave(f) : toast.error("Employee & course required")}
                    data-testid="save-enrollment-btn"
                >{i18n.t("batch1.efc007a393f6")}</Button>
            </DialogFooter>
        </DialogContent></Dialog>
    );
}
